import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ticketsAPI, teamAPI, agentsAPI, capsuleConfigAPI, formatApiError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Loader2, RefreshCw, LayoutGrid, Building2, GitBranch, ExternalLink,
  Trash2, Plus, Shield, Clock, ClipboardList, Activity, BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';
import { avisarDependenciasPendientes } from '@/lib/dependenciasPendientes';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';
import TaskCard from './TaskCard';
import TaskDialog from './TaskDialog';
import AutorizacionesDelCaso from './AutorizacionesDelCaso';
import DependencyPicker from './DependencyPicker';
import { TIPO_POR_OMISION } from '@/lib/tiposDeVinculo';
import { columnasDeTablero, PHASE_STATUS_COLORS, PHASE_STATUS_LABELS } from './constants';

export default function TicketBoard({ ticket, tenantId, capsuleId, onTicketRefresh, highlightTaskId, onHighlightDone }) {
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [entries, setEntries] = useState([]);
  const [coordinatorRuns, setCoordinatorRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  // **Arranca por fase, no por estado** (Diego, 2026-10-08). Son dos ejes distintos y el cartel
  // de abajo lo dice: el estado es el ciclo de vida de la solicitud, las fases son las etapas del
  // trabajo. Para quien tiene el caso abierto la pregunta es «en que etapa va», y esa es la fase.
  //
  // Y la vista por fase era **la mas completa de las dos** —tiene el selector de estado de la fase
  // y el «+ Subtarea» por columna— y estaba apagada por omision: lo mas util del tablero requeria
  // saber que habia un boton que lo prendia.
  const [viewMode, setViewMode] = useState('phase'); // status | phase
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  /**
   * El guardado de una tarea que **borra** dependencias, esperando confirmacion.
   *
   * La planificacion del caso ya confirmaba esto desde antes; la de una tarea no, y es el mismo
   * gesto: sacar una predecesora de la lista y guardar la borra. Con el Gantt pidiendo confirmar
   * para quitar una flecha (Paso 4), este era **el unico lugar donde un vinculo moria sin que
   * nadie dijera nada**.
   */
  const [tareaQueBorraVinculos, setTareaQueBorraVinculos] = useState(null);
  const [savingTask, setSavingTask] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamRoles, setTeamRoles] = useState([]);
  const [agents, setAgents] = useState([]);
  const [logTaskId, setLogTaskId] = useState(null);
  const [logMinutes, setLogMinutes] = useState('');
  // Obligatoria: si fuera opcional se saltearia siempre, y `consumed_hours` volveria a ser un
  // numero que crece sin que quede dicho de que. Ver `services/effort_tracking.py`.
  const [logNota, setLogNota] = useState('');
  const [horario, setHorario] = useState(null);
  const [refs, setRefs] = useState(ticket?.external_refs || []);
  const [newRef, setNewRef] = useState({ ref_type: 'issue', ref_id: '', project_id: '' });
  const [refBusy, setRefBusy] = useState(false);
  const [addPhaseKey, setAddPhaseKey] = useState(null);
  const [scheduleLinks, setScheduleLinks] = useState([]);
  const [ticketPredecessors, setTicketPredecessors] = useState([]);
  const [planningStart, setPlanningStart] = useState('');
  const [planningEnd, setPlanningEnd] = useState('');
  const [planningDepartment, setPlanningDepartment] = useState('');
  const [savingPlanning, setSavingPlanning] = useState(false);
  const [workflowTemplates, setWorkflowTemplates] = useState([]);
  const [departments, setDepartments] = useState([]);
  // Los estados de tarea son de la capsula, no de la plataforma. Viajan en el mismo documento
  // de configuracion que las plantillas y las gerencias, asi que se leen del mismo fetch.
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [changeTemplateOpen, setChangeTemplateOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [confirmChangeOpen, setConfirmChangeOpen] = useState(false);

  const load = async () => {
    if (!ticket?.ticket_id) return;
    setLoading(true);
    try {
      const [proj, ent, tsk, runs, links] = await Promise.all([
        ticketsAPI.getProject(tenantId, capsuleId, ticket.ticket_id),
        ticketsAPI.listTimeEntries(tenantId, capsuleId, ticket.ticket_id),
        ticketsAPI.listTasks(tenantId, capsuleId, ticket.ticket_id),
        ticketsAPI.listCoordinatorRuns(tenantId, capsuleId, ticket.ticket_id),
        ticketsAPI.listScheduleLinks(tenantId, capsuleId, { ticket_id: ticket.ticket_id }),
      ]);
      setProject(proj.data);
      setEntries(ent.data || []);
      setTasks(tsk.data || []);
      setCoordinatorRuns(runs.data || []);
      const linkRows = links.data || [];
      setScheduleLinks(linkRows);
      setPlanningStart(proj.data?.planned_start || '');
      setPlanningEnd(proj.data?.planned_end || '');
      setPlanningDepartment(proj.data?.requesting_department || '');
      setTicketPredecessors(
        linkRows
          .filter((l) => l.to_kind === 'ticket' && l.to_id === ticket.ticket_id)
          .map((l) => ({
            kind: l.from_kind,
            id: l.from_id,
            label: l.from_label || l.from_id,
            link_id: l.link_id,
            link_type: l.link_type,
            lag_dias: l.lag_dias,
          }))
      );
    } catch {
      toast.error('No se pudo cargar el tablero');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [ticket?.ticket_id, tenantId, capsuleId]);
  useEffect(() => { setRefs(ticket?.external_refs || []); }, [ticket?.ticket_id]);

  useEffect(() => {
    if (!highlightTaskId || loading) return undefined;
    const el = document.querySelector(`[data-testid="task-card-${highlightTaskId}"]`);
    if (!el) return undefined;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('ring-2', 'ring-brand-primary', 'ring-offset-1');
    const timer = setTimeout(() => {
      el.classList.remove('ring-2', 'ring-brand-primary', 'ring-offset-1');
      onHighlightDone?.();
    }, 3500);
    return () => clearTimeout(timer);
  }, [highlightTaskId, loading, tasks, onHighlightDone]);

  useEffect(() => {
    if (!tenantId || !capsuleId) return;
    teamAPI.list().then((r) => setTeamMembers(r.data || [])).catch(() => setTeamMembers([]));
    agentsAPI.list(tenantId, capsuleId).then((r) => setAgents(r.data || [])).catch(() => setAgents([]));
    capsuleConfigAPI.get(tenantId, capsuleId).then((r) => {
      setWorkflowTemplates(r.data?.workflow_templates || []);
      setDepartments(r.data?.requesting_departments || []);
      setTaskStatuses(r.data?.task_statuses || []);
      // Los roles del area: el panel de autorizaciones deja elegir uno como autorizante
      // —"el director", "autorizante de compras"— y sin esto el desplegable sale vacio.
      setTeamRoles(r.data?.team_roles || []);
      // La jornada del area, para que el dialogo pueda decir cuantos dias sale una tarea con
      // la dedicacion que se le ponga. Sin eso el porcentaje es un numero sin consecuencia
      // visible, y un numero asi se carga a ojo.
      setHorario(r.data?.horario_laboral || null);
    }).catch(() => {
      setWorkflowTemplates([]);
      setDepartments([]);
      setTaskStatuses([]);
      setTeamRoles([]);
      setHorario(null);
    });
  }, [tenantId, capsuleId]);

  const phases = project?.phases || [];
  const allTasksFlat = tasks.length ? tasks : [
    ...(project?.phases || []).flatMap((p) => p.tasks || []),
    ...(project?.unassigned_tasks || []),
  ];
  const taskList = tasks.length ? tasks : allTasksFlat;

  const applyWorkflow = async (params) => {
    setApplying(true);
    try {
      await ticketsAPI.applyWorkflow(tenantId, capsuleId, ticket.ticket_id, params);
      toast.success('Workflow aplicado');
      await load();
      if (onTicketRefresh) await onTicketRefresh();
    } catch (e) {
      toast.error(formatApiError(e) || 'Error al aplicar workflow');
    } finally {
      setApplying(false);
    }
  };

  const confirmChangeTemplate = async () => {
    if (!selectedTemplateId) return;
    setConfirmChangeOpen(false);
    setChangeTemplateOpen(false);
    await applyWorkflow({ template_id: selectedTemplateId, regenerate: 'true' });
  };

  const moveTaskStatus = async (taskId, status, task) => {
    try {
      const res = await ticketsAPI.patchTask(tenantId, capsuleId, ticket.ticket_id, taskId, { status });
      // El backend ya decide cuando hay predecesores sin terminar; filtrar aca por nombre de
      // estado hacia que el aviso no apareciera en una capsula con estados propios. Y el aviso
      // vive en un solo lugar: las otras dos pantallas que mueven estados lo tiraban.
      avisarDependenciasPendientes(res);
      await load();
    } catch {
      toast.error('Error al mover la tarea');
    }
  };

  const moveTaskPhase = async (taskId, phase) => {
    try {
      await ticketsAPI.patchTask(tenantId, capsuleId, ticket.ticket_id, taskId, { phase: phase || null });
      await load();
    } catch {
      toast.error('Error al cambiar la fase');
    }
  };

  const setPhaseStatus = async (phaseKey, status) => {
    if (!phases.length) return;
    const updated = phases.map((p) => ({
      key: p.key, label: p.label, order: p.order,
      status: p.key === phaseKey ? status : p.status,
      requires_verification: p.requires_verification,
    }));
    try {
      await ticketsAPI.updatePhases(tenantId, capsuleId, ticket.ticket_id, updated);
      await load();
      if (onTicketRefresh) await onTicketRefresh();
    } catch {
      toast.error('Error al actualizar la fase');
    }
  };

  const syncPredecessorLinks = async (toKind, toId, predecessors, existingLinks) => {
    const existing = (existingLinks || scheduleLinks).filter(
      (l) => l.to_kind === toKind && l.to_id === toId,
    );
    const desiredKeys = new Set(predecessors.map((p) => `${p.kind}:${p.id}`));
    for (const link of existing) {
      const key = `${link.from_kind}:${link.from_id}`;
      if (!desiredKeys.has(key)) {
        await ticketsAPI.deleteScheduleLink(tenantId, capsuleId, link.link_id);
      }
    }
    const porClave = new Map(existing.map((l) => [`${l.from_kind}:${l.from_id}`, l]));
    for (const p of predecessors) {
      const key = `${p.kind}:${p.id}`;
      const tipo = p.link_type || TIPO_POR_OMISION;
      const lag = Number(p.lag_dias) || 0;
      const ya = porClave.get(key);
      if (!ya) {
        await ticketsAPI.createScheduleLink(tenantId, capsuleId, {
          from_kind: p.kind,
          from_id: p.id,
          to_kind: toKind,
          to_id: toId,
          link_type: tipo,
          lag_dias: lag,
        });
      } else if ((ya.link_type || TIPO_POR_OMISION) !== tipo || (Number(ya.lag_dias) || 0) !== lag) {
        // **Cambiar el tipo no se hace creando de nuevo.** `create` deduplica por el par de
        // puntas: devuelve el vinculo que estaba e ignora el tipo del pedido, asi que elegir
        // otro tipo se guardaba y no pasaba nada. Eso es un boton que miente.
        await ticketsAPI.patchScheduleLink(tenantId, capsuleId, ya.link_id, {
          link_type: tipo,
          lag_dias: lag,
        });
      }
    }
  };

  const handleSaveTask = async (payload, taskId, meta, yaConfirmado = false) => {
    // **Guardar fechas no destruye nada; guardar menos dependencias de las que habia, si.** Se
    // confirma solo en ese caso y diciendo cuantas se van: una confirmacion que aparece siempre
    // se aprende a saltear.
    if (taskId && meta?.predecessors && !yaConfirmado) {
      const quedan = new Set(meta.predecessors.map((p) => `${p.kind}:${p.id}`));
      const seVan = scheduleLinks
        .filter((l) => l.to_kind === 'task' && l.to_id === taskId)
        .filter((l) => !quedan.has(`${l.from_kind}:${l.from_id}`));
      if (seVan.length) {
        setTareaQueBorraVinculos({ payload, taskId, meta, cuantas: seVan.length });
        return;
      }
    }
    setSavingTask(true);
    try {
      let savedId = taskId;
      if (taskId) {
        const res = await ticketsAPI.patchTask(tenantId, capsuleId, ticket.ticket_id, taskId, payload);
        if (res.data?.warnings?.length) {
          toast.warning(`Dependencias pendientes: ${res.data.warnings.join(', ')}`);
        }
        toast.success('Tarea actualizada');
      } else {
        const res = await ticketsAPI.createTask(tenantId, capsuleId, ticket.ticket_id, payload);
        savedId = res.data?.task_id;
        toast.success('Tarea creada');
      }
      if (savedId && meta?.predecessors) {
        await syncPredecessorLinks('task', savedId, meta.predecessors);
      }
      setTaskDialogOpen(false);
      setEditingTask(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || 'Error al guardar tarea');
    } finally {
      setSavingTask(false);
    }
  };

  /**
   * **El boton dice "Guardar" y puede borrar.** `syncPredecessorLinks` elimina los vinculos que
   * salieron de la lista: sacar una dependencia del selector y guardar la borra, y eso es parte del
   * plan que alguien armo. Guardar fechas no destruye nada; guardar **menos dependencias de las que
   * habia**, si. Se confirma solo en ese caso, y diciendo cuantas se van — una confirmacion que
   * aparece siempre se aprende a saltear.
   */
  const saveTicketPlanning = async () => {
    const existentes = scheduleLinks
      .filter((l) => l.to_kind === 'ticket' && l.to_id === ticket.ticket_id)
      .map((l) => l.from_id);
    const quedan = new Set((ticketPredecessors || []).map((p) => p.id ?? p.from_id ?? p));
    const seVan = existentes.filter((id) => !quedan.has(id));
    if (seVan.length && !confirmandoPlanificacion) {
      setConfirmandoPlanificacion(seVan.length);
      return;
    }
    setConfirmandoPlanificacion(0);
    setSavingPlanning(true);
    try {
      await ticketsAPI.patchPlanning(tenantId, capsuleId, ticket.ticket_id, {
        planned_start: planningStart || null,
        planned_end: planningEnd || null,
        requesting_department: planningDepartment || null,
      });
      await syncPredecessorLinks('ticket', ticket.ticket_id, ticketPredecessors);
      toast.success('Planificacion del ticket actualizada');
      await load();
      if (onTicketRefresh) await onTicketRefresh();
    } catch (e) {
      toast.error(formatApiError(e) || 'Error al guardar planificacion');
    } finally {
      setSavingPlanning(false);
    }
  };

  // Lo que esta por borrarse, para poder **decir que se lleva** en el dialogo. Un "¿seguro?" sin
  // contenido no agrega informacion: la agrega saber que son tres imputaciones y siete horas.
  const [tareaABorrar, setTareaABorrar] = useState(null);
  // Cuantas dependencias se van si se guarda. 0 = no hay nada que confirmar.
  const [confirmandoPlanificacion, setConfirmandoPlanificacion] = useState(0);
  const [imputacionABorrar, setImputacionABorrar] = useState(null);

  const getTaskBlockers = (taskId) => scheduleLinks
    .filter((l) => l.to_kind === 'task' && l.to_id === taskId && !l.from_complete)
    .map((l) => ({ label: l.from_label || l.from_id, link_id: l.link_id }));

  /**
   * Borrar una tarea **se lleva mas que la tarea**: el backend hace `time_entries.delete_many`,
   * borra sus `schedule_links` y sus avisos. Eran horas que cargo una persona, con su nota, y se
   * iban con **un clic sin confirmacion**, desde un icono de 12 px que esta **a 3 px del lapiz de
   * editar**. Era la peor de la pantalla y es la que esta tanda ataca primero.
   *
   * El dialogo no pregunta "¿seguro?": **dice que se lleva**. Es el molde del de cambiar plantilla,
   * que ya explica que conserva.
   */
  const handleDeleteTask = async (task) => {
    const horas = Number(task?.consumed_hours || 0);
    const links = scheduleLinks.filter(
      (l) => (l.to_kind === 'task' && l.to_id === task.task_id)
        || (l.from_kind === 'task' && l.from_id === task.task_id),
    ).length;
    setTareaABorrar({ task, horas, links });
  };

  const confirmarBorradoDeTarea = async () => {
    const task = tareaABorrar?.task;
    if (!task) return;
    try {
      await ticketsAPI.deleteTask(tenantId, capsuleId, ticket.ticket_id, task.task_id);
      toast.success('Tarea eliminada');
      setTareaABorrar(null);
      await load();
    } catch {
      toast.error('Error al eliminar tarea');
    }
  };

  const logHours = async (taskId) => {
    const mins = Math.round(parseFloat(logMinutes) * 60);
    if (!mins || mins <= 0) { toast.error('Indica horas > 0'); return; }
    if (!logNota.trim()) { toast.error('Falta decir en que se fueron esas horas'); return; }
    try {
      await ticketsAPI.createTimeEntry(tenantId, capsuleId, ticket.ticket_id, {
        task_id: taskId, minutes: mins, activity: logNota.trim(),
      });
      setLogTaskId(null); setLogMinutes(''); setLogNota('');
      toast.success('Esfuerzo registrado');
      await load();
      if (onTicketRefresh) await onTicketRefresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const confirmEntry = async (entryId) => {
    try {
      await ticketsAPI.updateTimeEntry(tenantId, capsuleId, ticket.ticket_id, entryId, { confirmed: true });
      await load();
    } catch {
      toast.error('Error al confirmar');
    }
  };

  /**
   * Borrar una imputacion borra **horas que cargo otra persona**, con su nota, sin rastro. Iba con
   * un clic, en un icono de 12 px sin `title` ni `data-testid`, al lado del de confirmar.
   */
  const deleteEntry = async () => {
    const entry = imputacionABorrar;
    if (!entry) return;
    try {
      await ticketsAPI.deleteTimeEntry(tenantId, capsuleId, ticket.ticket_id, entry.entry_id);
      setImputacionABorrar(null);
      await load();
    } catch {
      toast.error('Error al eliminar registro');
    }
  };

  const addRef = async () => {
    if (!newRef.ref_id.trim()) return;
    setRefBusy(true);
    try {
      await ticketsAPI.addExternalRef(tenantId, capsuleId, ticket.ticket_id, {
        system: 'gitlab', ref_type: newRef.ref_type, ref_id: newRef.ref_id.trim(),
        project_id: newRef.project_id.trim() || undefined,
      });
      setNewRef({ ref_type: 'issue', ref_id: '', project_id: '' });
      const res = await ticketsAPI.refreshExternalRefs(tenantId, capsuleId, ticket.ticket_id);
      setRefs(res.data?.external_refs || []);
      toast.success('Vinculo GitLab agregado');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRefBusy(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-brand-primary" /></div>;
  }

  // Las columnas y su significado salen del catalogo de la capsula. Antes era una lista fija
  // filtrada por nombre (`s !== 'failed' && s !== 'cancelled'`), asi que una capsula con
  // estados propios no tenia tablero.
  const columnas = columnasDeTablero(taskStatuses);
  const tasksByStatus = columnas.reduce((acc, e) => {
    acc[e.id] = taskList.filter((t) => t.status === e.id);
    return acc;
  }, {});

  const cardProps = {
    phases,
    taskStatuses,
    onStatusChange: (id, st) => moveTaskStatus(id, st, taskList.find((t) => t.task_id === id)),
    onPhaseChange: moveTaskPhase,
    onOpen: (t) => navigate(`/tickets/${ticket.ticket_id}/tasks/${t.task_id}?capsule=${capsuleId}`),
    onEdit: (t) => { setEditingTask(t); setTaskDialogOpen(true); },
    onDelete: handleDeleteTask,
    onLogHours: logHours,
    logTaskId,
    logMinutes,
    logNota,
    setLogTaskId,
    setLogMinutes,
    setLogNota,
    getTaskBlockers,
  };

  return (
    <div className="h-full overflow-auto p-4 space-y-4" data-testid="ticket-board">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-[10px] text-zinc-500 max-w-xl">
          <strong>Estado del ticket</strong> = ciclo de vida de la solicitud. <strong>Fases</strong> = etapas del pipeline de trabajo interno (no son lo mismo).
        </p>
        <Link
          to={`/executive/gantt?capsule=${encodeURIComponent(capsuleId)}`}
          className="text-[10px] text-brand-primary flex items-center gap-1 hover:underline"
          data-testid="gantt-link"
        >
          <BarChart3 className="w-3 h-3" /> Ver Gantt
        </Link>
        <Link
          to={`/executive?capsule=${encodeURIComponent(capsuleId)}`}
          className="text-[10px] text-brand-primary flex items-center gap-1 hover:underline"
          data-testid="workload-link"
        >
          <BarChart3 className="w-3 h-3" /> Ver carga del equipo
        </Link>
      </div>

      <Card className="border border-brand-border rounded-sm">
        <CardContent className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div>
            <span className="text-zinc-500 flex items-center gap-1"><Building2 className="w-3 h-3" /> Gerencia</span>
            <div className="font-medium mt-0.5">{planningDepartment || project?.requesting_department || '—'}</div>
          </div>
          <div>
            <span className="text-zinc-500">Plantilla</span>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="font-mono">{project?.workflow_template_id || '—'}</span>
              {workflowTemplates.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 text-[10px] rounded-sm"
                  data-testid="change-template-button"
                  onClick={() => {
                    setSelectedTemplateId(project?.workflow_template_id || workflowTemplates[0]?.template_id || '');
                    setChangeTemplateOpen(true);
                  }}
                >
                  Cambiar
                </Button>
              )}
            </div>
          </div>
          <div>
            <span className="text-zinc-500">Esfuerzo (est/real)</span>
            <div className="font-medium mt-0.5">{project?.estimated_effort_hours ?? '—'} / {project?.consumed_effort_hours ?? 0} h</div>
          </div>
          <div>
            <span className="text-zinc-500">Avance</span>
            <div className="mt-1 flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full bg-brand-primary" style={{ width: `${project?.progress_pct || 0}%` }} />
              </div>
              <span className="font-medium">{project?.progress_pct || 0}%</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-brand-border rounded-sm">
        <CardHeader className="py-2">
          <CardTitle className="text-xs font-semibold uppercase">Planificacion del ticket</CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-3 text-xs">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-zinc-500">Inicio planificado</label>
              <Input type="date" value={planningStart} onChange={(e) => setPlanningStart(e.target.value)} className="h-8 rounded-sm mt-0.5 text-xs" />
            </div>
            <div>
              <label className="text-[10px] text-zinc-500">Fin planificado</label>
              <Input type="date" value={planningEnd} onChange={(e) => setPlanningEnd(e.target.value)} className="h-8 rounded-sm mt-0.5 text-xs" />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-zinc-500">Gerencia solicitante</label>
            <select
              className="w-full border border-brand-border rounded-sm h-8 px-2 text-xs mt-0.5"
              value={planningDepartment}
              onChange={(e) => setPlanningDepartment(e.target.value)}
              data-testid="board-department-select"
            >
              <option value="">—</option>
              {departments.filter((d) => d?.id || d?.name).map((d) => (
                <option key={d.id || d.name} value={d.id || d.name}>{d.name || d.id}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-zinc-500">Este ticket espera a</label>
            {/* **Este no se acota al caso**, y es la diferencia con el de una tarea: un caso
                que espera a otro caso es legitimo y es la mitad de para que existen los
                vinculos. Lo que se acota es el selector de predecesoras de una tarea. */}
            <DependencyPicker
              tenantId={tenantId}
              capsuleId={capsuleId}
              predecessors={ticketPredecessors}
              onChange={setTicketPredecessors}
              excludeTicketId={ticket.ticket_id}
              toKind="ticket"
              toId={ticket.ticket_id}
              conTipoYEspera
            />
          </div>
          <Button size="sm" className="h-7 text-[10px] rounded-sm" onClick={saveTicketPlanning} disabled={savingPlanning}>
            {savingPlanning ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            Guardar planificacion
          </Button>
        </CardContent>
      </Card>

      {!phases.length ? (
        <div className="border border-dashed border-brand-border rounded-sm p-6 text-center space-y-3">
          <p className="text-xs text-zinc-500">Sin plan de trabajo. Aplica la plantilla segun el intent del ticket.</p>
          <Button size="sm" className="rounded-sm h-8 text-xs" disabled={applying} onClick={() => applyWorkflow()}>
            {applying ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <LayoutGrid className="w-3 h-3 mr-1" />}
            Generar plan de trabajo
          </Button>
        </div>
      ) : null}

      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-1">
          <Button size="sm" variant={viewMode === 'status' ? 'default' : 'outline'} className="h-7 text-[10px] rounded-sm" onClick={() => setViewMode('status')} data-testid="tablero-por-estado">
            Por estado
          </Button>
          <Button size="sm" variant={viewMode === 'phase' ? 'default' : 'outline'} className="h-7 text-[10px] rounded-sm" onClick={() => setViewMode('phase')} data-testid="tablero-por-fase">
            Por fase
          </Button>
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" className="h-7 text-[10px] rounded-sm" onClick={load}><RefreshCw className="w-3 h-3" /></Button>
          <Button size="sm" className="h-7 text-[10px] rounded-sm" onClick={() => { setEditingTask(null); setTaskDialogOpen(true); }} data-testid="create-task-btn">
            <Plus className="w-3 h-3 mr-1" /> Tarea
          </Button>
        </div>
      </div>

      {/* Las autorizaciones van **antes del tablero de tareas** y no como una columna mas: no
          son trabajo del area, son gente de afuera a la que hay que esperar. Mezclarlas con las
          tareas del equipo haria que se lean como algo que alguien de aca tiene que hacer. */}
      <div className="mb-3">
        <AutorizacionesDelCaso
          tareas={tasks}
          tenantId={tenantId} capsuleId={capsuleId} ticketId={ticket?.ticket_id}
          teamRoles={teamRoles} personas={teamMembers}
          onCambio={load}
        />
      </div>

      {viewMode === 'status' ? (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {columnas.map((estado) => (
            <div key={estado.id} className="min-w-[200px] w-[200px] shrink-0 border border-brand-border rounded-sm bg-zinc-50/50">
              <div className="p-2 border-b border-brand-border bg-white text-xs font-semibold text-zinc-700">
                {estado.name} ({(tasksByStatus[estado.id] || []).length})
              </div>
              <div className="p-2 space-y-2 min-h-[80px]">
                {(tasksByStatus[estado.id] || []).map((t) => (
                  <TaskCard key={t.task_id} task={t} pendingBlockers={getTaskBlockers(t.task_id)} {...cardProps} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {phases.map((ph) => (
            <div key={ph.key} className="min-w-[220px] w-[220px] shrink-0 border border-brand-border rounded-sm bg-zinc-50/50">
              <div className="p-2 border-b border-brand-border bg-white">
                <div className="flex items-center justify-between gap-1">
                  <span className="text-xs font-semibold truncate" title={ph.label}>{ph.label}</span>
                  {ph.requires_verification && <Shield className="w-3 h-3 text-amber-600" />}
                </div>
                <select
                  value={ph.status}
                  onChange={(e) => setPhaseStatus(ph.key, e.target.value)}
                  className={`mt-1 w-full rounded-sm px-1 py-0.5 text-[10px] border border-zinc-200 ${PHASE_STATUS_COLORS[ph.status] || ''}`}
                >
                  {/* Estaba en ingles y escrito a mano: `pending`, `in_progress`, `done`,
                      `skipped` tal cual, en el unico control que mueve el avance del caso. */}
                  {Object.entries(PHASE_STATUS_LABELS).map(([id, nombre]) => (
                    <option key={id} value={id}>{nombre}</option>
                  ))}
                </select>
              </div>
              <div className="p-2 space-y-2 min-h-[60px]">
                {taskList.filter((t) => t.phase === ph.key).map((t) => (
                  <TaskCard key={t.task_id} task={t} pendingBlockers={getTaskBlockers(t.task_id)} {...cardProps} />
                ))}
                {addPhaseKey === ph.key ? (
                  <Button size="sm" className="w-full h-6 text-[10px]" onClick={() => { setEditingTask(null); setTaskDialogOpen(true); setAddPhaseKey(null); }}>
                    Usar dialogo de tarea
                  </Button>
                ) : (
                  <button type="button" onClick={() => setAddPhaseKey(ph.key)} className="w-full text-[10px] text-zinc-400 hover:text-brand-primary py-1">
                    <Plus className="w-3 h-3 inline" /> Subtarea
                  </button>
                )}
              </div>
            </div>
          ))}
          {taskList.filter((t) => !t.phase).length > 0 && (
            <div className="min-w-[200px] border border-dashed border-brand-border rounded-sm p-2">
              <span className="text-[10px] font-semibold text-zinc-500">Sin fase</span>
              <div className="mt-2 space-y-2">
                {taskList.filter((t) => !t.phase).map((t) => (
                  <TaskCard key={t.task_id} task={t} pendingBlockers={getTaskBlockers(t.task_id)} {...cardProps} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {(project?.effort_by_phase || []).length > 0 && (
        <Card className="border border-brand-border rounded-sm">
          <CardHeader className="pb-2 py-2"><CardTitle className="text-xs font-semibold uppercase">Plan vs real por fase</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-[11px]">
              <thead className="bg-zinc-50"><tr>
                <th className="text-left p-2">Fase</th>
                <th className="text-right p-2">Est (h)</th>
                <th className="text-right p-2">Real (h)</th>
                <th className="text-right p-2">Tareas</th>
              </tr></thead>
              <tbody>
                {project.effort_by_phase.map((p) => (
                  <tr key={p.key} className="border-t border-zinc-100">
                    <td className="p-2">{p.label}</td>
                    <td className="p-2 text-right font-mono">{p.estimate_hours}</td>
                    <td className="p-2 text-right font-mono">{p.consumed_hours}</td>
                    <td className="p-2 text-right font-mono">{p.done}/{p.tasks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {entries.length > 0 && (
        <div className="space-y-2">
          <span className="text-xs font-semibold uppercase text-zinc-600">Registros de esfuerzo</span>
          <div className="border border-brand-border rounded-sm overflow-hidden max-h-40 overflow-y-auto">
            <table className="w-full text-[11px]">
              <tbody>
                {entries.map((e) => (
                  <tr key={e.entry_id} className="border-b border-zinc-100">
                    <td className="p-2 font-mono text-[10px]">{e.date}</td>
                    <td className="p-2">{e.actor_name || '—'}</td>
                    {/* La nota es la mitad del registro: sin ella el numero no se puede
                        discutir. Las viejas no la tienen y **se dice**, no se disimula. */}
                    <td className="p-2 max-w-[16rem]">
                      {e.activity
                        ? <span className="truncate block" title={e.activity}>{e.activity}</span>
                        : <span className="text-zinc-400 italic">sin nota</span>}
                    </td>
                    <td className="p-2 text-right">{(e.minutes / 60).toFixed(2)}h</td>
                    <td className="p-2">{e.confirmed ? 'ok' : <button type="button" className="text-amber-700 underline" onClick={() => confirmEntry(e.entry_id)}>confirmar</button>}</td>
                    <td className="p-2">
                      <button type="button" title="Eliminar este registro de horas"
                        aria-label="Eliminar este registro de horas"
                        data-testid={`borrar-imputacion-${e.entry_id}`}
                        onClick={() => setImputacionABorrar(e)}>
                        <Trash2 className="w-3 h-3 text-zinc-400" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Card className="border border-brand-border rounded-sm">
        <CardHeader className="py-2 flex flex-row justify-between">
          <CardTitle className="text-xs font-semibold uppercase flex items-center gap-1"><GitBranch className="w-3 h-3" /> GitLab</CardTitle>
        </CardHeader>
        <CardContent className="p-3 space-y-2 text-[11px]">
          {refs.filter((r) => (r.system || 'gitlab') === 'gitlab').map((r) => (
            <div key={`${r.ref_type}-${r.ref_id}`} className="flex items-center gap-2">
              <Badge variant="outline" className="text-[9px]">{r.ref_type} #{r.ref_id}</Badge>
              {r.ref_url && <a href={r.ref_url} target="_blank" rel="noreferrer"><ExternalLink className="w-3 h-3" /></a>}
            </div>
          ))}
          <div className="flex gap-1">
            <select value={newRef.ref_type} onChange={(e) => setNewRef((s) => ({ ...s, ref_type: e.target.value }))} className="border rounded-sm text-[10px] px-1">
              <option value="issue">Issue</option>
              <option value="merge_request">MR</option>
            </select>
            <Input value={newRef.ref_id} onChange={(e) => setNewRef((s) => ({ ...s, ref_id: e.target.value }))} placeholder="IID" className="h-7 text-[10px] w-20" />
            <Button size="sm" className="h-7 text-[10px]" onClick={addRef} disabled={refBusy}>Vincular</Button>
          </div>
        </CardContent>
      </Card>

      {coordinatorRuns.length > 0 && (
        <div className="space-y-2">
          <span className="text-xs font-semibold uppercase flex items-center gap-1"><Activity className="w-3 h-3" /> Coordinador</span>
          <div className="border border-brand-border rounded-sm max-h-32 overflow-y-auto text-[10px]">
            {coordinatorRuns.slice(0, 10).map((r) => (
              <div key={r.run_id} className="px-2 py-1 border-b border-zinc-100 font-mono">
                {r.created_at?.slice(0, 16)} — {r.rounds ?? 0} rondas
              </div>
            ))}
          </div>
        </div>
      )}

      <TaskDialog
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        task={editingTask}
        phases={phases}
        taskStatuses={taskStatuses}
        teamMembers={teamMembers}
        agents={agents}
        onSave={handleSaveTask}
        saving={savingTask}
        horario={horario}
        tenantId={tenantId}
        capsuleId={capsuleId}
        ticketId={ticket?.ticket_id}
        incomingLinks={scheduleLinks}
        tareasDelCaso={taskList}
      />

      <Dialog open={changeTemplateOpen} onOpenChange={setChangeTemplateOpen}>
        <DialogContent className="rounded-sm border-brand-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Cambiar plantilla</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-zinc-500">Las tareas generadas por la plantilla anterior se reemplazaran. Las tareas manuales se conservan.</p>
          <select
            data-testid="change-template-select"
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
            className="w-full border border-brand-border rounded-sm h-9 px-2 text-sm bg-white mt-2"
          >
            {workflowTemplates.map((t) => (
              <option key={t.template_id} value={t.template_id}>{t.name || t.template_id}</option>
            ))}
          </select>
          <Button
            className="w-full mt-3 bg-brand-primary text-white rounded-sm"
            disabled={!selectedTemplateId || applying}
            onClick={() => setConfirmChangeOpen(true)}
          >
            Aplicar plantilla
          </Button>
        </DialogContent>
      </Dialog>

      <ConfirmActionDialog
        open={confirmChangeOpen}
        onOpenChange={setConfirmChangeOpen}
        title="Reemplazar plan de trabajo"
        description="Se eliminaran las subtareas creadas por la plantilla anterior y se generaran las de la nueva plantilla. Las tareas agregadas manualmente no se tocan."
        confirmText="Cambiar plantilla"
        // Reemplazar el plan no destruye trabajo de nadie y lo dice arriba: no va en rojo.
        destructivo={false}
        onConfirm={confirmChangeTemplate}
      />

      <ConfirmActionDialog
        open={!!tareaABorrar}
        onOpenChange={(abierto) => { if (!abierto) setTareaABorrar(null); }}
        title="Eliminar la tarea"
        description={tareaABorrar
          ? `«${tareaABorrar.task.title}» se elimina para siempre, y con ella `
            + [
              tareaABorrar.horas > 0
                ? `las ${tareaABorrar.horas} h cargadas contra ella, con sus notas`
                : 'sus registros de horas (no tiene ninguno)',
              tareaABorrar.links > 0
                ? `${tareaABorrar.links} dependencia(s) del plan`
                : null,
              'sus avisos',
            ].filter(Boolean).join(', ')
            + '. No se puede deshacer.'
          : ''}
        confirmText="Eliminar la tarea"
        onConfirm={confirmarBorradoDeTarea}
      />

      <ConfirmActionDialog
        open={confirmandoPlanificacion > 0}
        onOpenChange={(abierto) => { if (!abierto) setConfirmandoPlanificacion(0); }}
        title="Guardar la planificación"
        description={`Se van a borrar ${confirmandoPlanificacion} dependencia(s) que estaban en el `
          + 'plan y ya no figuran en la lista. Las fechas se guardan igual.'}
        confirmText="Guardar y borrarlas"
        onConfirm={saveTicketPlanning}
      />

      <ConfirmActionDialog
        open={!!tareaQueBorraVinculos}
        onOpenChange={(abierto) => { if (!abierto) setTareaQueBorraVinculos(null); }}
        title="Guardar la tarea"
        description={tareaQueBorraVinculos
          ? `Se van a borrar ${tareaQueBorraVinculos.cuantas} dependencia(s) que estaban en el `
            + 'plan y ya no figuran en la lista. El resto de la tarea se guarda igual.'
          : ''}
        confirmText="Guardar y borrarlas"
        onConfirm={() => {
          const pendiente = tareaQueBorraVinculos;
          setTareaQueBorraVinculos(null);
          if (pendiente) {
            handleSaveTask(pendiente.payload, pendiente.taskId, pendiente.meta, true);
          }
        }}
      />

      <ConfirmActionDialog
        open={!!imputacionABorrar}
        onOpenChange={(abierto) => { if (!abierto) setImputacionABorrar(null); }}
        title="Eliminar el registro de horas"
        description={imputacionABorrar
          ? `Se eliminan ${(imputacionABorrar.minutes / 60).toFixed(2)} h cargadas por `
            + `${imputacionABorrar.actor_name || 'otra persona'}`
            + (imputacionABorrar.activity ? ` («${imputacionABorrar.activity}»)` : ' (sin nota)')
            + '. No queda rastro y no se puede deshacer.'
          : ''}
        confirmText="Eliminar el registro"
        onConfirm={deleteEntry}
      />
    </div>
  );
}
